import FlexContainer from 'core-system/FlexContainer'
import React from 'react'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/config/store'
import styled from 'styled-components'
import Text from 'core-system/Text'
import FormattingUtils from 'shared/FormattingUtils'
import palette from 'core-system/Themes/palette'
import * as FlexIcons from 'core-system/Icons/Flex'
import Divider from 'core-system/Divider'
import SvgTooltip from 'core-system/Tooltip/SvgTooltip'
import InfoIcon from 'core-system/Icons/Misc/Info'

const IconContainer = styled(FlexContainer)`
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  margin-right: 0.25rem;
`

const renderUpcomingAllocation = (
  amount: number,
  maxAmount: number,
  type: 'Transit' | 'Parking' | 'ORCA',
  icon: React.ReactNode,
  employerMaxAllocationLimit?: number
) => {
  const showTooltip = type === 'Transit' || type === 'Parking'
  const isEmployerLimit = employerMaxAllocationLimit != null
  const tooltipDescription = isEmployerLimit
    ? "This max is set by your employer's allocation limit for this benefit, minus any employer contributions and your deductions."
    : 'This max is set by the IRS monthly limit for pre-tax benefits, minus any employer contributions and your deductions.'

  return (
    <FlexContainer flexDirection='column' alignItems='center'>
      {/* HEADER */}
      <FlexContainer flexDirection='row' alignItems='center'>
        <IconContainer center>{icon}</IconContainer>
        <Text variant='action1'>{type}</Text>
      </FlexContainer>
      {/* AMOUNT */}
      <FlexContainer
        flexDirection='column'
        alignItems='center'
        marginLeft='1rem'
      >
        <Text variant='h2' textColor={palette.primary.pink800}>
          {FormattingUtils.formatDollar(amount, 0)}
        </Text>
        <FlexContainer alignItems='center'>
          <Text variant='action3' textColor={palette.text.placeholder}>
            {`(max. ${FormattingUtils.formatDollar(maxAmount, 0)})`}
          </Text>
          {showTooltip && (
            <SvgTooltip
              svg={
                <InfoIcon
                  color={palette.text.placeholder}
                  height={14}
                  width={14}
                />
              }
              description={tooltipDescription}
              position='right'
              marginLeft='0.25rem'
              display='flex'
              alignItems='center'
            />
          )}
        </FlexContainer>
      </FlexContainer>
    </FlexContainer>
  )
}

interface UpcomingAllocationsProps {
  hasORCA: boolean
  maxTransitAllocation: number
  maxParkingAllocation: number
  maxTransitAllocationORCA: number
}

const UpcomingAllocations = React.memo((props: UpcomingAllocationsProps) => {
  const {
    hasORCA,
    maxTransitAllocation,
    maxParkingAllocation,
    maxTransitAllocationORCA,
  } = props

  const { preTaxPrograms } = useSelector(
    (state: AppState) => state.employeePlatform
  )

  return (
    <FlexContainer
      flexDirection='row'
      alignItems='center'
      justifyContent='space-around'
      marginBottom='0.5rem'
      marginX='3rem'
    >
      {preTaxPrograms.COMMUTER_ORCA && hasORCA
        ? renderUpcomingAllocation(
            preTaxPrograms.COMMUTER_ORCA?.employeeDeduction?.employeeDeduction,
            maxTransitAllocationORCA,
            'ORCA',
            <FlexIcons.Transit color={palette.text.secondary} />
          )
        : null}
      {preTaxPrograms.COMMUTER_ORCA && hasORCA ? (
        <Divider size='5rem' direction='vertical' margin='0 1.5rem' />
      ) : null}
      {preTaxPrograms.COMMUTER_TRANSIT
        ? renderUpcomingAllocation(
            preTaxPrograms.COMMUTER_TRANSIT?.employeeDeduction
              ?.employeeDeduction,
            maxTransitAllocation,
            'Transit',
            <FlexIcons.Transit color={palette.text.secondary} />,
            preTaxPrograms.COMMUTER_TRANSIT?.employerMaxAllocationLimit
          )
        : null}
      {preTaxPrograms.COMMUTER_TRANSIT && preTaxPrograms.COMMUTER_PARKING ? (
        <Divider size='5rem' direction='vertical' margin='0 1.5rem' />
      ) : null}
      {preTaxPrograms.COMMUTER_PARKING
        ? renderUpcomingAllocation(
            preTaxPrograms.COMMUTER_PARKING?.employeeDeduction
              ?.employeeDeduction,
            maxParkingAllocation,
            'Parking',
            <FlexIcons.Parking color={palette.text.secondary} />,
            preTaxPrograms.COMMUTER_PARKING?.employerMaxAllocationLimit
          )
        : null}
      {!preTaxPrograms.COMMUTER_PARKING && !preTaxPrograms.COMMUTER_TRANSIT ? (
        <FlexContainer alignItems='center'>
          <Text variant='h4' textColor={palette.text.placeholder}>
            NO ACTIVE PRE-TAX COMMUTER BENEFITS PROGRAMS
          </Text>
        </FlexContainer>
      ) : null}
    </FlexContainer>
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  UpcomingAllocations.displayName = 'UpcomingAllocations'
}

export default UpcomingAllocations
